import { Injectable } from '@angular/core';

import { catchError, map, switchMap } from 'rxjs/operators';
import { BehaviorSubject, Observable, of } from 'rxjs';
import * as _ from 'underscore';

import { UsersApi } from '../../../api/users/users.api';
import { AdminAccountsFoldersApi } from '../../../api/adminAccountsFolders/adminAccountsFolders.api';

import { AccountModel } from '../../../Core/Users/models/account.model';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { gateCall } from '../../../Core/rxjs-utils';

import { Folder } from '../../Shared/models/folder.model';

@Injectable()
export class CopyMentionsToAccountService {

    private folders: Folder[] = [];
    private foldersProvider: BehaviorSubject<Folder[]>;

    private loadingFolders: boolean = false;
    private loadingFoldersProvider: BehaviorSubject<boolean>;

    constructor(
        private usersApi: UsersApi,
        private adminAccountsFoldersApi: AdminAccountsFoldersApi,
        private currentUserStateService: CurrentUserStateService,
    ) {
        this.createProviders();
    }
    /**
     * Sets up all of the providers used in this service
     */
    private createProviders(): void {
        this.foldersProvider = new BehaviorSubject<Folder[]>(this.folders);
        this.loadingFoldersProvider = new BehaviorSubject<boolean>(this.loadingFolders);
    }

    /**
     * Reloads the folders from the server
     */
    public getAllAccountFolders(accountId: number): void {
        this.setFoldersLoading(true);
        // check to see if the user has adv mon permissions before making the api call
        const request = gateCall(this.currentUserStateService.hasMonitoringAccess()).pipe(
            switchMap(() => this.adminAccountsFoldersApi.getCurrentUserAppPrefs(accountId)),
            map((apiFolders) => {
                const folders = apiFolders.map(apiFolder => new Folder(apiFolder));
                this.setFoldersLoading(false);
                this.setAccountFolders(folders);

                return folders;
            }),
            catchError(() => {
                this.setFoldersLoading(false);
                this.setAccountFolders([]);
                return of([]);
            })
        );

        request.subscribe();
    }

    /**
     * Gets the folders provider
     */
    public getAccountFolders(): Observable<Folder[]> {
        return this.foldersProvider;
    }

    /**
     * Updates the folders
     */
    private setAccountFolders(folders: Folder[]) {
        this.folders = folders;
        this.foldersProvider.next(folders);
    }

    /**
     * appends new folder to folders
     */
    public appendAccountFolder(folder: Folder) {
        this.folders.push(folder);
        this.foldersProvider.next(this.folders);
    }

    /**
     * returns if getting folders is loading
     */
    public getFoldersLoading(): Observable<boolean> {
        return this.loadingFoldersProvider;
    }

    /**
     * setting folders is loading
     */
    private setFoldersLoading(loading: boolean) {
        this.loadingFolders = loading;
        this.loadingFoldersProvider.next(loading);
    }


    /**
     * returns a list of accounts after the user passes a string to search on (name or ID)
     */
    searchAccounts(searchString: string): Observable<AccountModel[]> {
        return this.usersApi.searchAccounts(searchString).pipe(
            map(accounts => {
                // we only want to return active accounts
                return accounts.map(account => new AccountModel(account)).filter(account =>
                    account.active && !account.isExpiryDatePast
                );
            })
        );
    }
}
